import type { Clip, ImageMask, Transform } from '../types';

interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
  imageWidth: number;
  imageHeight: number;
}

const PREVIEW_WIDTH = 1280;
const PREVIEW_HEIGHT = 720;

/**
 * Computes the transform for a generated mask so it aligns exactly
 * over the cropped area in the source clip.
 */
export function computeMaskOverlayTransform(sourceClip: Clip, mask: ImageMask): Transform {
  if (!mask.cropRect) {
    return {
      x: 0,
      y: 0,
      scale: 1,
      rotation: sourceClip.transform?.rotation ?? 0,
    };
  }

  const { x, y, width, height, imageWidth, imageHeight } = mask.cropRect as CropRect;
  const canvasRatio = PREVIEW_WIDTH / PREVIEW_HEIGHT;
  const imageRatio = imageWidth / imageHeight;

  let renderWidth: number;
  let renderHeight: number;
  if (imageRatio > canvasRatio) {
    renderWidth = PREVIEW_WIDTH;
    renderHeight = PREVIEW_WIDTH / imageRatio;
  } else {
    renderHeight = PREVIEW_HEIGHT;
    renderWidth = PREVIEW_HEIGHT * imageRatio;
  }

  const imageOffsetX = (PREVIEW_WIDTH - renderWidth) / 2;
  const imageOffsetY = (PREVIEW_HEIGHT - renderHeight) / 2;

  const scaleToCanvas = renderWidth / imageWidth;
  const cropCanvasX = imageOffsetX + (x * scaleToCanvas);
  const cropCanvasY = imageOffsetY + (y * scaleToCanvas);
  const cropCanvasWidth = width * scaleToCanvas;

  const maskRatio = width / height;
  let maskRenderWidth: number;
  let maskRenderHeight: number;
  if (maskRatio > canvasRatio) {
    maskRenderWidth = PREVIEW_WIDTH;
    maskRenderHeight = PREVIEW_WIDTH / maskRatio;
  } else {
    maskRenderHeight = PREVIEW_HEIGHT;
    maskRenderWidth = PREVIEW_HEIGHT * maskRatio;
  }

  const targetScale = cropCanvasWidth / maskRenderWidth;
  const targetCenterX = cropCanvasX + cropCanvasWidth / 2;
  const targetCenterY = cropCanvasY + (height * scaleToCanvas) / 2;

  const sourceScale = sourceClip.transform?.scale ?? 1;
  const sourceX = sourceClip.transform?.x ?? 0;
  const sourceY = sourceClip.transform?.y ?? 0;

  const offsetX = targetCenterX - PREVIEW_WIDTH / 2;
  const offsetY = targetCenterY - PREVIEW_HEIGHT / 2;

  return {
    x: (offsetX * sourceScale) + sourceX,
    y: (offsetY * sourceScale) + sourceY,
    scale: targetScale * sourceScale,
    rotation: sourceClip.transform?.rotation ?? 0,
  };
}
